import './index.css'

import cn from 'clsx'
import {
  CompositeDecorator,
  convertFromRaw,
  convertToRaw,
  Editor,
  EditorState,
  RawDraftContentState,
  RichUtils,
} from 'draft-js'
import isEmpty from 'lodash/isEmpty'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import exportToHtml from '@disco/content-editor/html-export'

import FocusWithin from 'components/ui/focus-within'
import Label from 'components/ui/label'

import Link from './Link'
import Toolbar from './Toolbar'

function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity()
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === 'LINK'
    )
  }, callback)
}

const decorator = new CompositeDecorator([
  {
    component: Link,
    strategy: findLinkEntities,
  },
])
type Props = {
  autoFocus?: boolean
  className?: string
  contentClassName?: string
  disabled?: boolean
  initialContent?: RawDraftContentState | null | undefined
  label?: React.ReactNode
  placeholder?: string
  required?: boolean
  tooltipId?: string
  onBlur?: () => void
  onChange?: (rawContent: RawDraftContentState, html: string) => void
  ds?: boolean
}

/**
 * A rich text HTML editor component built with Draft.js that supports bold, italic, underline, and link formatting.
 * The editor outputs both raw Draft.js content state and HTML format, with optional label and validation support.
 *
 * @param autoFocus - Whether the editor should automatically focus on mount
 * @param className - Optional CSS class name for the editor wrapper
 * @param contentClassName - Optional CSS class name for the content area
 * @param disabled - Whether the editor is disabled/read-only
 * @param initialContent - Initial content to populate the editor (raw Draft.js content state)
 * @param label - Optional label to display above the editor
 * @param placeholder - Placeholder text to show when editor is empty
 * @param required - Whether the field is required (affects label styling)
 * @param tooltipId - Optional tooltip ID for accessibility
 * @param onBlur - Callback fired when the editor loses focus
 * @param onChange - Callback fired when content changes, receives raw content and HTML
 * @param ds - Whether to use design system styling
 * @returns The HTML editor component with toolbar and formatting controls
 */
const HTMLEditor = ({
  autoFocus,
  className,
  contentClassName,
  disabled,
  initialContent,
  label,
  placeholder,
  required,
  tooltipId,
  onBlur,
  onChange,
  ds,
}: Props): React.ReactNode => {
  const editorRef = useRef<Editor>(null)
  const [editorState, setEditorState] = useState(() => {
    if (
      initialContent &&
      typeof initialContent === 'object' &&
      !isEmpty(initialContent)
    ) {
      return EditorState.createWithContent(
        convertFromRaw(initialContent),
        decorator,
      )
    } else {
      return EditorState.createEmpty(decorator)
    }
  })
  const [isFocused, setIsFocused] = useState<boolean>(false)
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus()
    }
  }, [autoFocus, disabled])
  const focusEditor = useCallback(() => {
    if (!isFocused && editorRef.current) {
      editorRef.current.focus()
    }
  }, [editorRef, isFocused])
  useEffect(() => {
    if (onChange) {
      const contentState = editorState.getCurrentContent()
      onChange(convertToRaw(contentState), exportToHtml(contentState))

      if (!contentState.hasText()) {
        onChange(null, '')
      }
    }
  }, [editorState])
  const handleBlur = useCallback(() => {
    setIsFocused(false)

    if (onBlur) {
      onBlur()
    }
  }, [onBlur])
  const handleFocus = useCallback(() => setIsFocused(true), [])
  const handleKeyCommand = useCallback(
    (command: string, editorState: EditorState) => {
      const state = RichUtils.handleKeyCommand(editorState, command)

      if (state) {
        setEditorState(state)
        return 'handled'
      }

      return 'not-handled'
    },
    [],
  )
  return (
    <FocusWithin onBlur={handleBlur} onFocus={handleFocus}>
      <div className={cn('HTMLEditor', className)} onMouseDown={focusEditor}>
        {label && (
          <Label caption={label} required={required} tooltipId={tooltipId} />
        )}
        <div
          className={cn('HTMLEditor-container', {
            'HTMLEditor-container--disabled': disabled,
            'HTMLEditor-container--focused': isFocused,
            'HTMLEditor-container--ds': ds,
          })}
          style={{
            borderRadius: 'var(--radius-2)',
          }}
        >
          <div className={cn('HTMLEditor-content', contentClassName)}>
            <Editor
              editorState={editorState}
              handleKeyCommand={handleKeyCommand}
              placeholder={placeholder}
              preserveSelectionOnBlur
              readOnly={disabled}
              ref={editorRef}
              onChange={setEditorState}
            />
          </div>
          <div className="HTMLEditor-toolbar">
            <Toolbar
              disabled={disabled}
              editorState={editorState}
              onChange={setEditorState}
            />
          </div>
        </div>
      </div>
    </FocusWithin>
  )
}

export default HTMLEditor
